/**
 * Routes that the edge router (CloudFront + Lambda@Edge) serves from the new
 * frontend. Navigating to one of them from inside this app must be a full page
 * load, so the edge can hand the request over; rendering it here would show the
 * old page.
 *
 * A full page load of such a route that still reached this app (the edge for that
 * environment does not hand it over yet) must keep rendering here, or the page
 * would reload forever. That is what the current pathname check is for.
 *
 * Pages know their company from the URL. The scope-company middleware adds
 * `?company=` (the company selected on the current page, else the active one)
 * to any route whose meta asks for it, by redirecting to the same route with the
 * query filled in. This guard runs before the middleware, so a hand-off on the
 * first pass would leave the company behind and the new frontend would fall back
 * to the user's default company (OPS-196). While the destination still needs a
 * company that the query does not carry, the guard stands aside; the middleware
 * redirect brings the navigation back here with the full query, and only then is
 * it turned into a page load.
 */
export const NEW_FRONTEND_ROUTES = ['wall-chart']

/**
 * @param  {Route}  to               destination route
 * @param  {String} currentPathname  window.location.pathname at the time of the guard
 * @return {Boolean} true when the navigation must become a full page load
 */
export function needsFullNavigation(to, currentPathname) {
  return (
    NEW_FRONTEND_ROUTES.includes(to.name) &&
    currentPathname !== to.path &&
    !awaitsCompany(to)
  )
}

/**
 * True while the scope-company middleware still has to add `?company=` to this
 * route. Mirrors that middleware's own condition, minus the auth check: a
 * signed-out visitor falls through to the normal middleware chain either way.
 *
 * @param  {Route} to
 * @return {Boolean}
 */
function awaitsCompany(to) {
  return Boolean(to.meta && to.meta.company) && !(to.query && to.query.company)
}
